import React, { useState, useEffect } from 'react';
import './Booking.css';

import { Heading } from '../../../components';
import { tours } from '../../../constants';

import { FaUser, FaEnvelope, FaPhone, FaGlobe, FaUsers, FaComment } from 'react-icons/fa';
import { FaCalendarDays } from 'react-icons/fa6';

import axios from "axios";
import { useSearchParams } from 'react-router-dom';
import Select from 'react-select'; // Correct import


const API_PATH = 'https://altours-react.asdesignsgalaxy.com/api/phpmailer/booking.php';

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;


const Booking = () => {

  const [searchParams] = useSearchParams();
  const tourTitle = searchParams.get('tour') || null;

  const tourOptions = tours.map(tour => ({
    value: tour.title.replace(/\s+/g, '_'),  // Example: "Komodo National Park" → "Komodo_National_Park"
    label: tour.title
  }));


  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    tour: tourTitle || '',
    departureDate: '',
    numberOfPeople: '',
    instructions: ''
  });

  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];
      
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.tour) validationErrors.push('Tour selection is required');
      if (!formData.departureDate) validationErrors.push('Departure date is required');
      if (!formData.numberOfPeople) validationErrors.push('Number of People is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        tour: tourTitle || '', // Preserve tour title if it was passed
        departureDate: '',
        numberOfPeople: '',
        instructions: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      tour: selectedOption.value
    }));
  };

  // Add today's date as min for departure date
  const today = new Date().toISOString().split('T')[0];

  return (
    <section className="booking linear-bg">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>
            <Heading title={"Book Tour"} subTitle={"Book Your Adventure"} sideHeading linear />

            <div className="input-box">
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="number"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Your Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {tourTitle ? (
                <div className="input-field">
                  <label htmlFor="tour"><FaGlobe /></label>
                  <input
                    type="text"
                    name="tour"
                    className="box"
                    id="tour"
                    value={formData.tour}
                    readOnly
                    disabled
                  />
                  <input type="hidden" name="tour" value={tourTitle} />
                </div>
              ) : (
                <div className="select">
                  <div className="select-container">
                    <label htmlFor="tour"><FaGlobe /></label>
                    <Select
                      id="tour"
                      options={tourOptions}
                      value={tourOptions.find(opt => opt.value === formData.tour)}
                      onChange={handleSelectChange}
                      className="select-box"
                      classNamePrefix="select-box-prefix"
                      placeholder="Select Tours"
                      isDisabled={isSubmitting}
                      required
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="input-box">
              <div className="input-field">
                <label htmlFor="departureDate"><FaCalendarDays /></label>
                <input
                  type="text"
                  name="departureDate"
                  className="box"
                  id="departureDate"
                  min={today}  // Prevent selecting past dates
                  placeholder="Departure Date"
                  value={formData.departureDate}
                  onChange={handleInputChange}
                  onFocus={(e) => e.target.type = 'date'}
                  onBlur={(e) => e.target.type = 'text'}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="input-field">
                <label htmlFor="numberOfPeople"><FaUsers /></label>
                <input
                  type="number"
                  name="numberOfPeople"
                  className="box"
                  id="numberOfPeople"
                  placeholder="Number of People"
                  value={formData.numberOfPeople}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-field text-area">
              <label htmlFor="instructions"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="instructions"
                className="box"
                id="instructions"
                placeholder="Additional Instructions"
                value={formData.instructions}
                onChange={handleInputChange}
                disabled={isSubmitting}
              ></textarea>
            </div>

            <button type="submit" className="btn" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && <div className="alert success"><span>Booking Submitted Successfully</span></div>}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Booking;